// Короткая сводка варианта для шапки: даты и цепочка событий поездки.
// Чистая функция от AppState.
import type { AppState, TripVariant } from '../../types';

/**
 * Элемент сводки. Идут в том же порядке, что и сегменты варианта, — сводка
 * должна читаться как маршрут: перелёт, переезд, ночёвка, снова перелёт.
 */
export type SummaryItem =
  | { kind: 'route'; airports: string[] }
  | { kind: 'rides'; count: number }
  | { kind: 'nights'; count: number };

export interface VariantSummary {
  /** '27.12' для одного перелёта, '27.12 – 16.01' если крайние даты разные, '' если перелётов нет */
  dates: string;
  items: SummaryItem[];
  flights: number;
  /** переезды + поездки на такси (у такси считается quantity) */
  rides: number;
  /** суммарно ночей во всех lodging варианта */
  nights: number;
}

/** '2026-12-27T11:55' -> '27.12'. Непарсимую строку отдаём как есть. */
function ddmm(local: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(local);
  if (!m) return local;
  return `${m[3]}.${m[2]}`;
}

export function computeVariantSummary(variant: TripVariant, state: AppState): VariantSummary {
  const items: SummaryItem[] = [];
  let firstDate = '';
  let lastDate = '';
  let flights = 0;
  let rides = 0;
  let nights = 0;

  /** Последний элемент, если он нужного вида — соседние однотипные события сливаются. */
  function tail<K extends SummaryItem['kind']>(kind: K): Extract<SummaryItem, { kind: K }> | null {
    const last = items[items.length - 1];
    return last && last.kind === kind ? (last as Extract<SummaryItem, { kind: K }>) : null;
  }

  for (const id of variant.segmentIds) {
    const seg = state.library[id];
    if (!seg) continue;

    switch (seg.kind) {
      case 'flight': {
        flights += 1;
        const date = ddmm(seg.departLocal);
        if (!firstDate) firstDate = date;
        lastDate = date;

        // Цепочка продолжается, только если прилёт совпал с вылетом следующего:
        // иначе сводка врала бы о непрерывности маршрута.
        const route = tail('route');
        if (route && route.airports[route.airports.length - 1] === seg.from) {
          route.airports.push(seg.to);
        } else {
          items.push({ kind: 'route', airports: [seg.from, seg.to] });
        }
        break;
      }
      case 'transfer':
      case 'taxi': {
        const count = seg.kind === 'taxi' ? Math.max(1, seg.quantity) : 1;
        rides += count;
        const prev = tail('rides');
        if (prev) prev.count += count;
        else items.push({ kind: 'rides', count });
        break;
      }
      case 'lodging': {
        nights += seg.nights;
        const prev = tail('nights');
        if (prev) prev.count += seg.nights;
        else items.push({ kind: 'nights', count: seg.nights });
        break;
      }
      default:
        break;
    }
  }

  let dates = '';
  if (firstDate) dates = lastDate && lastDate !== firstDate ? `${firstDate} – ${lastDate}` : firstDate;

  return { dates, items, flights, rides, nights };
}
